import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  getWebStoreButtonImage,
  messages,
  type SupportedLocale,
} from "./i18n";
import { listWebShopOffers, loadSession, type ShopSession } from "./api";
import "./home.css";

const HOME_ASSET_BASE = `${import.meta.env.BASE_URL}home-assets/img/`;

function homeAsset(name: string): string {
  return `${HOME_ASSET_BASE}${name}`;
}

export default function HomePage({
  locale,
  session,
}: {
  locale: SupportedLocale;
  session: ShopSession | null;
}) {
  const ctaRef = useRef<HTMLAnchorElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const [storedSession] = useState<ShopSession | null>(() =>
    session ? null : loadSession(),
  );
  const effectiveSession = session ?? storedSession;
  const [hasAvailableOffers, setHasAvailableOffers] = useState<boolean | null>(
    effectiveSession ? null : true,
  );
  const webStoreImage = getWebStoreButtonImage(locale);
  const text = messages[locale];
  const showStoreCta = hasAvailableOffers === true;

  useEffect(() => {
    if (!effectiveSession) {
      setHasAvailableOffers(true);
      return;
    }

    let canceled = false;
    setHasAvailableOffers(null);
    listWebShopOffers(effectiveSession, {
      country: effectiveSession.country ?? "US",
      locale: effectiveSession.xsollaLocale ?? locale,
    })
      .then((offers) => {
        if (!canceled) setHasAvailableOffers(offers.length > 0);
      })
      .catch(() => {
        if (!canceled) setHasAvailableOffers(true);
      });

    return () => {
      canceled = true;
    };
  }, [effectiveSession, locale]);

  useEffect(() => {
    if (!showStoreCta) return;

    const cta = ctaRef.current;
    const footer = footerRef.current;
    if (!cta || !footer) return;

    const updateStoreCtaOffset = () => {
      cta.style.setProperty("--footer-avoid-offset", "0px");

      const ctaRect = cta.getBoundingClientRect();
      const footerRect = footer.getBoundingClientRect();
      const overlap = Math.max(0, ctaRect.bottom - footerRect.top + 12);

      cta.style.setProperty("--footer-avoid-offset", `${overlap}px`);
    };

    const queueUpdate = () => window.requestAnimationFrame(updateStoreCtaOffset);

    window.addEventListener("scroll", queueUpdate, { passive: true });
    window.addEventListener("resize", queueUpdate);
    window.addEventListener("load", updateStoreCtaOffset);
    updateStoreCtaOffset();

    return () => {
      window.removeEventListener("scroll", queueUpdate);
      window.removeEventListener("resize", queueUpdate);
      window.removeEventListener("load", updateStoreCtaOffset);
    };
  }, [showStoreCta]);

  return (
    <div className="home-page">
      <main id="top">
        <section className="home-hero" aria-label={text.homeHeroLabel}>
          <div className="home-hero-scene" aria-hidden="true">
            <div className="home-character-frame">
              <div className="home-enemy-stack">
                <img
                  className="home-enemy-layer home-enemy-right"
                  src={homeAsset("enemy-right.webp")}
                  alt=""
                />
                <img
                  className="home-enemy-layer home-enemy-body"
                  src={homeAsset("enemy.webp")}
                  alt=""
                />
                <img
                  className="home-enemy-layer home-enemy-left"
                  src={homeAsset("enemy-left.webp")}
                  alt=""
                />
              </div>
              <div className="home-archer-stack">
                <img
                  className="home-archer-layer home-archer-body"
                  src={homeAsset("archer.webp")}
                  alt=""
                />
                <img
                  className="home-archer-layer home-archer-bow"
                  src={homeAsset("archer-bow.webp")}
                  alt=""
                />
              </div>
            </div>
          </div>

          <div className="home-hero-logo-wrap">
            <img
              className="home-hero-logo"
              src={homeAsset("logo.webp")}
              alt={text.homeHeroLabel}
            />
          </div>
        </section>
      </main>

      {showStoreCta ? (
        <Link
          ref={ctaRef}
          className="home-store-cta"
          to="/shop"
          aria-label={text.homeWebStoreAria}
        >
          <img src={homeAsset(webStoreImage)} alt={text.homeWebStoreAlt} />
        </Link>
      ) : null}

      <footer className="home-site-footer" ref={footerRef}>
        <div className="home-footer-downloads" aria-label={text.homeOfficialStoreLinks}>
          <div className="home-app-icon-tile" aria-hidden="true">
            <img src={homeAsset("app-icon.png")} alt={text.homeHeroLabel} />
          </div>
          <a
            className="home-store-button"
            href="https://apps.apple.com/app/id6742771024"
            target="_blank"
            rel="noreferrer"
            aria-label="App Store"
          >
            <img src={homeAsset("icon-store-appstore.svg")} alt="App Store" />
          </a>
          <a
            className="home-store-button"
            href="https://play.google.com/store/apps/details?id=com.merge.shooter"
            target="_blank"
            rel="noreferrer"
            aria-label="Google Play"
          >
            <img
              src={homeAsset("icon-store-googleplay.svg")}
              alt="Google Play"
            />
          </a>
        </div>
        <div className="home-footer-meta">
          <p>&copy; 2026 Geisha Tokyo, Inc.</p>
        </div>
      </footer>
    </div>
  );
}
